import { useInjection } from 'inversify-react';
import { useCallback, useEffect, useState } from 'react';
import { Badge, Button, Spinner, Table } from 'react-bootstrap';
import { API } from '../services/HTTP';

const ALGOS_RUN_ENDPOINT = 'algos/run';
const ALGOS_RUNS_ENDPOINT = 'algos/runs';

type RunStatus = 'running' | 'success' | 'error';

type Run = {
    id: string;
    status: RunStatus;
    message: string;
    created_at: string;
};

function StatusBadge({ status }: { status: RunStatus }) {
    if (status === 'running') return <Badge bg="secondary"><Spinner animation="border" size="sm" /> Running</Badge>;
    if (status === 'success') return <Badge bg="success">Success</Badge>;
    return <Badge bg="danger">Error</Badge>;
}

export function AlgosPage() {
    const api: API = useInjection<API>('API');
    const [runs, setRuns] = useState<Run[]>([]);
    const [isHistoryLoading, setIsHistoryLoading] = useState(true);
    const [isRunning, setIsRunning] = useState(false);

    const loadHistory = useCallback(async () => {
        setIsHistoryLoading(true);
        try {
            const response = await api.get(ALGOS_RUNS_ENDPOINT);
            setRuns(JSON.parse(response.data) as Run[]);
        } catch (err) {
            console.error('Failed to load run history:', err);
        } finally {
            setIsHistoryLoading(false);
        }
    }, [api]);

    useEffect(() => {
        loadHistory();
    }, [loadHistory]);

    const triggerRun = useCallback(async () => {
        setIsRunning(true);
        try {
            await api.post(ALGOS_RUN_ENDPOINT, JSON.stringify({}));
        } catch (err) {
            // Error run is still persisted by the backend — fall through to reload.
        } finally {
            await loadHistory();
            setIsRunning(false);
        }
    }, [api, loadHistory]);

    const latestRun = isRunning ? null : runs[0] ?? null;

    return <>
        <h1>Algos</h1>

        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
            <Button onClick={triggerRun} disabled={isRunning}>
                {isRunning
                    ? <><Spinner animation="border" size="sm" />&nbsp;Running…</>
                    : 'Run Algorithm'}
            </Button>

            {isRunning && <span><Badge bg="secondary"><Spinner animation="border" size="sm" /> Running</Badge></span>}

            {!isRunning && latestRun && (
                <span>
                    Last run:&nbsp;<StatusBadge status={latestRun.status} />
                    {latestRun.message && (
                        <span className="text-muted" style={{ marginLeft: '0.5rem', fontSize: '0.9em' }}>
                            {latestRun.message}
                        </span>
                    )}
                </span>
            )}
        </div>

        <h5>Run History</h5>

        {isHistoryLoading ? (
            <Spinner animation="border" />
        ) : runs.length === 0 ? (
            <p className="text-muted">No runs yet.</p>
        ) : (
            <Table striped bordered hover size="sm">
                <thead>
                    <tr>
                        <th>#</th>
                        <th>Time</th>
                        <th>Status</th>
                        <th>Message</th>
                    </tr>
                </thead>
                <tbody>
                    {runs.map((run, index) => (
                        <tr key={run.id}>
                            <td>{runs.length - index}</td>
                            <td>{new Date(run.created_at).toLocaleString()}</td>
                            <td><StatusBadge status={run.status} /></td>
                            <td>{run.message}</td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        )}
    </>;
}

